export type PresetColor = Readonly<{ value: string; name: string; ref?: string }>

/**
 * Maps a stored content color ref to a CSS value. Palette refs become live
 * var() references (kept current by ThemeStyleVariables) without checking the
 * palette: an unset color's var still exists and resolves to 'transparent'.
 * A given fallback is emitted into the var() reference for contexts without
 * the theme's custom properties. The neutrals have no CSS var and resolve to
 * literal hex regardless of the fallback. Only refs that are neither
 * `colors.<name>`-shaped nor a neutral return undefined. Examples:
 * `colorRefToCssValue('colors.color3')` → `var(--colors-color3)`,
 * `colorRefToCssValue('colors.color3', 'inherit')` → `var(--colors-color3, inherit)`,
 * `colorRefToCssValue('blackColor', 'inherit')` → `#000000`.
 */
export function colorRefToCssValue(ref: string | null | undefined, fallback?: string): string | undefined {
  if (!ref) return undefined
  if (ref === 'blackColor') return '#000000'
  if (ref === 'whiteColor') return '#ffffff'
  const colorName = /^colors\.(color\d+)$/.exec(ref)?.[1]
  if (!colorName) return undefined
  return fallback ? `var(--colors-${colorName}, ${fallback})` : `var(--colors-${colorName})`
}

/**
 * Parses a `var(--colors-colorN, ...)` reference back to its palette ref;
 * null for any other value. The inverse of colorRefToCssValue's palette
 * branch.
 */
export function cssVarToColorRef(value: string): string | null {
  const colorName = /^var\(--colors-(color\d+)[,)]/.exec(value)?.[1]
  return colorName ? `colors.${colorName}` : null
}

/**
 * The display contract of the paired content color fields: a custom color
 * takes precedence over the ref, and with neither set the field default
 * applies. An empty-string custom color counts as unset, matching
 * resolveRefColor.
 */
export function resolveColorValue(
  custom: string | null | undefined,
  ref: string | null | undefined,
  fallback: string,
): string {
  return custom || colorRefToCssValue(ref) || fallback
}

/**
 * Maps each preset's ref to its color value, for resolving a stored ref to
 * its display color.
 */
export function getRefColors(presetColors: readonly PresetColor[]): Record<string, string> {
  return Object.fromEntries(presetColors.filter((preset) => preset.ref).map((preset) => [preset.ref, preset.value]))
}

type ResolveRefColorOptions = {
  /**
   * Ref to fall back to when the stored ref is not among the presets or
   * nothing is stored. Banner fields set it to the same default their canvas
   * resolver (resolveColorOption) uses; var()-rendered fields leave it unset.
   */
  defaultRef?: string
  /** Color to display when nothing else resolves. */
  fallback?: string
}

/**
 * Resolves what a paired color/ref field displays: the color to show and
 * which preset swatch is selected. The result matches what the field's
 * canvas renders for the same stored data.
 */
export function resolveRefColor(
  currentCustomColor: string | null | undefined,
  currentColorRef: string | null | undefined,
  refColors: Record<string, string>,
  { defaultRef, fallback = 'transparent' }: ResolveRefColorOptions = {},
): { value: string; selectedRef: string | null } {
  if (currentCustomColor) return { value: currentCustomColor, selectedRef: null }

  if (currentColorRef && refColors[currentColorRef]) {
    return { value: refColors[currentColorRef], selectedRef: currentColorRef }
  }

  if (defaultRef) return { value: refColors[defaultRef] ?? fallback, selectedRef: defaultRef }

  // A palette ref missing from the presets renders on the canvas as a var()
  // resolving to 'transparent' while the color is unset; match that. (The
  // neutrals also render but are always among the presets, so they never
  // reach this branch.)
  if (colorRefToCssValue(currentColorRef)) return { value: 'transparent', selectedRef: null }

  return { value: fallback, selectedRef: null }
}
